import { useAppSelector } from 'app/hooks';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import SelectTargetMenuModal from 'components/SelectTargetMenuModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { DisplayControlGroup, MenuButtonActivation, MenuDisplayRestriction } from 'features/DisplayControlGroup/models';
import { useEffect, useRef, useState } from 'react';
import { Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { VscArrowRight } from 'react-icons/vsc';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { TARGET_MENU_DISPLAY_CONTROL_CONDITION, TARGET_MENU_DISPLAY_CONTROL_TIMING } from 'utils';
import service from './services';
import './styles.scss';

interface ControlGroupProps {
  id: number;
  isVisible: boolean;
  onClose: () => void;
}

interface ControlGroupRequest {
  display_control_group: number;
  menu_ids: number[];
}

const ControlGroupModal = ({ id, isVisible, onClose }: ControlGroupProps) => {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // states
  const [isShowModalAddMenuRestriction, setIsShowModalAddMenuRestriction] = useState(false);
  const [exceptMenuIdsRestriction, setExceptMenuIdsRestriction] = useState<number[]>([]);

  const [isShowModalAddMenuActivation, setIsShowModalAddMenuActivation] = useState(false);
  const [exceptMenuIdsActivation, setExceptMenuIdsActivation] = useState<number[]>([]);

  const [menuButtonActivations, setMenuButtonActivations] = useState<MenuButtonActivation[]>([]);
  const [menuDisplayRestrictions, setMenuDisplayRestrictions] = useState<MenuDisplayRestriction[]>([]);

  const initialStateRestriction = menuDisplayRestrictions.reduce((o, key) => ({ ...o, [key.id]: false }), {});
  const [checkedRestriction, setCheckedRestriction] = useState<any>(initialStateRestriction);

  const initialStateActivations = menuDisplayRestrictions.reduce((o, key) => ({ ...o, [key.id]: false }), {});
  const [checkedActivations, setCheckedActivations] = useState<any>(initialStateActivations);
  const [controlGroup, setControlGroup] = useState<DisplayControlGroup>({
    id: 0,
    name: '',
    code: '',
    display_name: '',
    display_name_ja: '',
    display_name_en: '',
    display_name_ko: '',
    display_name_zh_hant: '',
    display_name_zh_hans: '',
    display_name_vi: '',
    note: '',
    delete_flg: false,
    menu_button_activations: [],
    menu_display_restrictions: [],
  });

  const refBtnDeleteRestriction = useRef<HTMLButtonElement>(null);
  const refBtnDeleteActivation = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let initialStateRestriction = menuDisplayRestrictions.reduce((o, key) => ({ ...o, [key.id]: false }), {});
    setCheckedRestriction(initialStateRestriction);
  }, [menuDisplayRestrictions]);

  useEffect(() => {
    let initialStateActivations = menuButtonActivations.reduce((o, key) => ({ ...o, [key.id]: false }), {});
    setCheckedActivations(initialStateActivations);
  }, [menuButtonActivations]);

  useEffect(() => {
    function handleDisableBtnDeleteRestriction() {
      let count = Object.keys(checkedRestriction).filter((key) => checkedRestriction[key] === true).length;
      if (count > 0) {
        return false;
      }
      return true;
    }
    if (handleDisableBtnDeleteRestriction()) {
      if (refBtnDeleteRestriction.current) refBtnDeleteRestriction.current.disabled = true;
    } else {
      if (refBtnDeleteRestriction.current) refBtnDeleteRestriction.current.disabled = false;
    }
  }, [refBtnDeleteRestriction, checkedRestriction]);

  useEffect(() => {
    function handleDisableBtnDeleteActivation() {
      let count = Object.keys(checkedActivations).filter((key) => checkedActivations[key] === true).length;
      if (count > 0) {
        return false;
      }
      return true;
    }
    if (handleDisableBtnDeleteActivation()) {
      if (refBtnDeleteActivation.current) refBtnDeleteActivation.current.disabled = true;
    } else {
      if (refBtnDeleteActivation.current) refBtnDeleteActivation.current.disabled = false;
    }
  }, [refBtnDeleteActivation, checkedActivations]);

  useEffect(() => {
    async function fetchData() {
      await service.get(id).then((res) => {
        setControlGroup(res.data);
        setMenuButtonActivations(res.data.menu_button_activations || []);
        setMenuDisplayRestrictions(res.data.menu_display_restrictions || []);
      });
    }
    fetchData();
  }, [id]);

  function handleAddMenuRestriction() {
    setIsShowModalAddMenuRestriction(true);
    setExceptMenuIdsRestriction(menuDisplayRestrictions.map((restriction) => restriction.menu));
  }
  function handleAddMenuActivation() {
    setIsShowModalAddMenuActivation(true);
    setExceptMenuIdsActivation(menuButtonActivations.map((activation) => activation.menu));
  }

  function handleCloseAddMenuRestriction() {
    setIsShowModalAddMenuRestriction(false);
  }

  function handleCloseAddMenuActivation() {
    setIsShowModalAddMenuActivation(false);
  }

  function onReflectRestriction(menuIds: number[]) {
    setIsShowModalAddMenuRestriction(false);
    const data: Partial<ControlGroupRequest> = {
      display_control_group: id,
      menu_ids: menuIds,
    };
    service.createRestriction(data as ControlGroupRequest).then((res) => {
      setMenuDisplayRestrictions(res.data);
    });
  }

  function onReflectActivation(menuIds: number[]) {
    setIsShowModalAddMenuActivation(false);
    const data: Partial<ControlGroupRequest> = {
      display_control_group: id,
      menu_ids: menuIds,
    };
    service.createActivation(data as ControlGroupRequest).then((res) => {
      setMenuButtonActivations(res.data);
    });
  }

  function toggleCheckRestriction(value: number) {
    let newState = { ...checkedRestriction };
    newState[value] = !newState[value];
    setCheckedRestriction(newState);
  }

  function toggleCheckActivation(value: number) {
    let newState = { ...checkedActivations };
    newState[value] = !newState[value];
    setCheckedActivations(newState);
  }

  function handleDeleteRestriction() {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('DisplayControlGroup.Confirm.Delete_Restriction')}
        onConfirm={processDeleteRestriction}
      />
    ));
  }

  async function processDeleteRestriction() {
    const idsArr = Object.keys(checkedRestriction).filter((key) => {
      if (checkedRestriction[key] === true) {
        return parseInt(key);
      }
      return null;
    });
    const ids = idsArr.map((value) => {
      return parseInt(value);
    });
    const body = { display_control_group: id, ids: ids };

    await service
      .deleteRestrictions(body)
      .then(async (res) => {
        setMenuDisplayRestrictions(res.data);
        showGlobalModal &&
          showGlobalModal(() => (
            <AlertModal
              title={t('DisplayControlGroup.Validator.success')}
              message={t('DisplayControlGroup.Validator.delete_success')}
            ></AlertModal>
          ));
      })
      .catch((err: any) => {
        <AlertModal
          title={t('GeDisplayControlGroupnre.Validator.error')}
          message={t('DisplayControlGroup.Validator.500')}
        ></AlertModal>;
      });
  }

  function handleDeleteActivation() {
    showGlobalModal(() => (
      <ConfirmModal
        title={t('App.confirm')}
        message={t('DisplayControlGroup.Confirm.Delete_Activations')}
        onConfirm={processDeleteActivation}
      />
    ));
  }

  async function processDeleteActivation() {
    const idsArr = Object.keys(checkedActivations).filter((key) => {
      if (checkedActivations[key] === true) {
        return parseInt(key);
      }
      return null;
    });
    const ids = idsArr.map((value) => {
      return parseInt(value);
    });
    const body = { display_control_group: id, ids: ids };

    await service
      .deleteActivations(body)
      .then(async (res) => {
        setMenuButtonActivations(res.data);
        showGlobalModal &&
          showGlobalModal(() => (
            <AlertModal
              title={t('DisplayControlGroup.Validator.success')}
              message={t('DisplayControlGroup.Validator.delete_success')}
            ></AlertModal>
          ));
      })
      .catch(() => {
        showGlobalModal &&
          showGlobalModal(() => (
            <AlertModal
              title={t('DisplayControlGroup.Validator.error')}
              message={t('DisplayControlGroup.Validator.delete_error')}
            ></AlertModal>
          ));
      });
  }

  return (
    <Modal
      className="modal-container custom-map-modal"
      show={isVisible}
      onHide={onClose}
      size="lg"
      fullscreen="lg-down"
      backdrop="static"
      centered
    >
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <div className="col-auto">
          <div className="mb-3">
            <FeatureTitleBar title={t('DisplayControlGroup.ModalControlGroup.Title')} />
          </div>
          <div>
            {t('DisplayControlGroup.ModalControlGroup.Group')} {controlGroup?.name}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <div className="row">
          {isShowModalAddMenuRestriction && (
            <SelectTargetMenuModal
              targetSourceType={TARGET_MENU_DISPLAY_CONTROL_CONDITION}
              exceptMenuIds={exceptMenuIdsRestriction}
              onReflect={onReflectRestriction}
              onClose={handleCloseAddMenuRestriction}
            />
          )}
          {isShowModalAddMenuActivation && (
            <SelectTargetMenuModal
              targetSourceType={TARGET_MENU_DISPLAY_CONTROL_TIMING}
              exceptMenuIds={exceptMenuIdsActivation}
              onReflect={onReflectActivation}
              onClose={handleCloseAddMenuActivation}
            />
          )}
          <div className="col-md-5">
            <div className="col-auto">
              <p>{t('DisplayControlGroup.ModalControlGroup.TableLeft.Title')}</p>
              <p> {t('DisplayControlGroup.ModalControlGroup.TableLeft.Desc')}</p>
            </div>
            <Row className="mb-2">
              <Col sm={2} lg={2}></Col>
              <Col sm={10} lg={10} className="d-flex justify-content-end">
                <button type="button" className="btn btn--dark-green" onClick={handleAddMenuRestriction}>
                  {t('DisplayControlGroup.ModalControlGroup.TableLeft.Btn_Add')}
                </button>
                <span className="ps-2 pe-2"></span>
                <button
                  type="button"
                  className="btn btn--red"
                  onClick={handleDeleteRestriction}
                  ref={refBtnDeleteRestriction}
                >
                  {t('DisplayControlGroup.ModalControlGroup.TableLeft.Btn_Del')}
                </button>
              </Col>
            </Row>
            <div className="d-flex flex-column" style={{ height: '352px', backgroundColor: '#999' }}>
              <div className="dan-table">
                <div className="dan-thead">
                  <div className="dan-tr">
                    <div className="dan-th" style={{ width: '10%' }}>
                      {t('DisplayControlGroup.ModalControlGroup.TableLeft.selection')}
                    </div>
                    <div className="dan-th border-start-0" style={{ width: '30%' }}>
                      {t('DisplayControlGroup.ModalControlGroup.TableLeft.code')}
                    </div>
                    <div className="dan-th border-start-0" style={{ width: '60%' }}>
                      {t('DisplayControlGroup.ModalControlGroup.TableLeft.name')}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-grow-1 flex-shrink-1 display-control-group">
                <AutoSizer>
                  {({ height, width }) => (
                    <FixedSizeList
                      className="fixed-size-list"
                      width={width}
                      height={height}
                      itemSize={32}
                      itemCount={menuDisplayRestrictions.length}
                      itemData={menuDisplayRestrictions}
                    >
                      {({ style, index }) => {
                        const restriction = menuDisplayRestrictions[index];

                        return (
                          <div key={index} id={`row-${index}`} className={`dan-tr d-flex`} style={style}>
                            <div className="dan-td d-flex justify-content-center" style={{ width: '10%' }}>
                              <Form.Check
                                type="checkbox"
                                value={restriction.id}
                                checked={checkedRestriction[restriction.id]}
                                onChange={() => toggleCheckRestriction(restriction.id)}
                              />
                            </div>
                            <div className="dan-td d-flex justify-content-center" style={{ width: '30%' }}>
                              {restriction.code}
                            </div>
                            <div className="dan-td d-flex justify-content-center" style={{ width: '60%' }}>
                              {restriction.name}
                            </div>
                          </div>
                        );
                      }}
                    </FixedSizeList>
                  )}
                </AutoSizer>
              </div>
            </div>
          </div>
          <div className="col-md-2 text-center mt-4" style={{ paddingTop: '5rem' }}>
            <div>
              <VscArrowRight size={100} />
            </div>
            <p>
              <span dangerouslySetInnerHTML={{ __html: t('DisplayControlGroup.ModalControlGroup.TableCenter.Desc') }} />
            </p>
          </div>
          <div className="col-md-5">
            <div className="col-12">
              <p>{t('DisplayControlGroup.ModalControlGroup.TableRight.Title')}</p>
              <p> {t('DisplayControlGroup.ModalControlGroup.TableRight.Desc')}</p>
            </div>
            <Row className="mb-2">
              <Col sm={2} lg={2}></Col>
              <Col sm={10} lg={10} className="d-flex justify-content-end">
                <button type="button" className="btn btn--dark-green" onClick={handleAddMenuActivation}>
                  {t('DisplayControlGroup.ModalControlGroup.TableRight.Btn_Add')}
                </button>
                <span className="ps-2 pe-2"></span>
                <button
                  type="button"
                  className="btn btn--red"
                  ref={refBtnDeleteActivation}
                  onClick={handleDeleteActivation}
                >
                  {t('DisplayControlGroup.ModalControlGroup.TableRight.Btn_Del')}
                </button>
              </Col>
            </Row>
            <div className="d-flex flex-column" style={{ height: '352px', backgroundColor: '#999' }}>
              <div className="dan-table">
                <div className="dan-thead">
                  <div className="dan-tr">
                    <div className="dan-th" style={{ width: '10%' }}>
                      {t('DisplayControlGroup.ModalControlGroup.TableRight.selection')}
                    </div>
                    <div className="dan-th border-start-0" style={{ width: '30%' }}>
                      {t('DisplayControlGroup.ModalControlGroup.TableRight.code')}
                    </div>
                    <div className="dan-th border-start-0" style={{ width: '60%' }}>
                      {t('DisplayControlGroup.ModalControlGroup.TableRight.name')}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex-grow-1 flex-shrink-1 display-control-group">
                <AutoSizer>
                  {({ height, width }) => (
                    <FixedSizeList
                      className="fixed-size-list"
                      width={width}
                      height={height}
                      itemSize={32}
                      itemCount={menuButtonActivations.length}
                      itemData={menuButtonActivations}
                    >
                      {({ style, index }) => {
                        const activation = menuButtonActivations[index];

                        return (
                          <div key={index} id={`row-${index}`} className={`dan-tr d-flex`} style={style}>
                            <div className="dan-td d-flex justify-content-center" style={{ width: '10%' }}>
                              <Form.Check
                                type="checkbox"
                                value={activation.id}
                                checked={checkedActivations[activation.id]}
                                onChange={() => toggleCheckActivation(activation.id)}
                              />
                            </div>
                            <div className="dan-td d-flex justify-content-center" style={{ width: '30%' }}>
                              {activation.code}
                            </div>
                            <div className="dan-td d-flex justify-content-center" style={{ width: '60%' }}>
                              {activation.name}
                            </div>
                          </div>
                        );
                      }}
                    </FixedSizeList>
                  )}
                </AutoSizer>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ControlGroupModal;
